import React from 'react';
import { motion, AnimatePresence  } from 'framer-motion';
import { ArrowRight, Calendar, Users, Award, Utensils } from 'lucide-react';
import { Button } from '../components/ui/button';
import { heroImages, specialties } from '../mockData';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/PageTransition';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { useNavigate } from 'react-router-dom';
import { BengaliDecorativeElement, BengaliCornerDecor, BengaliDivider } from '../components/BengaliPatterns';
import MainDish from "../assets/mainDish.png";
import TopDish from "../assets/Top.png";
import BottomDish from "../assets/2.png";
import ExtraDish from "../assets/1.png";
import { useEffect, useState } from "react";
import bgVideo from "../assets/download.mp4"; 

const images = [
  MainDish,
  TopDish,
  BottomDish,
  ExtraDish,
];
export const HomePage = () => {
 const [index, setIndex] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, 3000); // change every 3 sec

    return () => clearInterval(interval);
  }, []);


  const stats = [
    { icon: Users, value: 500, suffix: '+', label: 'Happy Customers' },
    { icon: Award, value: 15, suffix: '+', label: 'Years Experience' },
    { icon: Utensils, value: 50, suffix: '+', label: 'Dishes' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Parallax */}
   <section className="min-h-screen flex items-center bg-cover bg-center relative " >
      <video
    autoPlay
    
    muted
    playsInline
    className="absolute top-0 left-0 w-full h-full object-cover  z-0"
  >
    <source src={bgVideo} type="video/mp4" />
  </video>
      <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center z-10 ">
        
        {/* LEFT CONTENT */}
        <div style={{ marginTop: '110px' }}>
          <h1 className="text-5xl lg:text-6xl font-bold text-black mb-6">
           A Taste of Bengal, Served with Tradition
          </h1>
          <p className="text-gray-600 mb-6">
           Authentic Bengali dishes crafted with traditional recipes, rich spices, and a touch of home.
          </p>
          <button className="bg-red-600 text-white px-6 py-3 rounded-full">
            ORDER NOW
          </button>
        </div>

        {/* RIGHT IMAGE ANIMATION */}
        <div className="relative h-[400px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.img
              key={images[index]}
              src={images[index]}
              className="w-500 h-500 object-cover rounded-full "
              
              initial={{ opacity: 0, x: 100, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -100, scale: 0.9 }}
              
              transition={{ duration: 0.6 }}
            />
          </AnimatePresence>
        </div>
      </div>
    </section>

      {/* Stats Section */}
      <FadeIn>
        <section className="py-16 bg-amber-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                >
                  <stat.icon className="w-12 h-12 text-amber-300 mx-auto mb-4" />
                  <div className="text-4xl font-bold text-white mb-2">
                    <AnimatedCounter 
                      end={stat.value} 
                      suffix={stat.suffix}
                      duration={2}
                    />
                  </div>
                  <p className="text-amber-100 text-lg">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </FadeIn>

      {/* Featured Specialties */}
      <section className="py-20 bg-gradient-to-b from-white to-amber-50/30 relative overflow-hidden">
        <BengaliDecorativeElement pattern="alpona" />
        <BengaliCornerDecor />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-amber-900 mb-4">
                Our Signature Dishes
              </h2>
              <BengaliDivider />
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Discover our signature Bengali dishes, crafted with love and authentic flavors
              </p>
            </div>
          </FadeIn>

          <StaggerContainer staggerDelay={0.15}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {specialties.map((item) => (
                <StaggerItem key={item.id}>
                  <motion.div
                    whileHover={{ y: -10, scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-500 bg-white border-amber-100">
                      <div className="relative h-64 overflow-hidden">
                        <motion.img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <Badge className="absolute top-4 right-4 bg-amber-600 hover:bg-amber-700 text-white">
                          {item.category}
                        </Badge>
                      </div>

                      <CardContent className="p-6">
                        <h3 className="text-xl font-bold text-amber-900 mb-2 group-hover:text-amber-700 transition-colors duration-300">
                          {item.title}
                        </h3>
                        <p className="text-sm text-gray-500 italic mb-3">{item.name}</p>
                        <p className="text-gray-600 mb-4 line-clamp-2">
                          {item.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-2xl font-bold text-amber-700">
                            {item.price}
                          </span>
                          <button className="px-4 py-2 bg-amber-100 hover:bg-amber-700 text-amber-900 hover:text-white rounded-lg transition-all duration-300 text-sm font-medium">
                            Order Now
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </StaggerItem>
              ))}
            </div>
          </StaggerContainer>

          <FadeIn delay={0.4}>
            <div className="text-center mt-12">
              <Button
                onClick={() => navigate('/menu')}
                className="bg-amber-700 hover:bg-amber-800 text-white px-8 py-3 text-lg rounded-lg transition-all duration-300 hover:scale-105"
              >
                View Full Menu
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA Section */}
      <FadeIn>
        <section className="py-20 bg-gradient-to-r from-amber-900 to-amber-800 relative overflow-hidden">
          <BengaliDecorativeElement pattern="lotus"  />
          <motion.div
            className="absolute inset-0 opacity-10"
            animate={{
              backgroundPosition: ['0% 0%', '100% 100%'],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
            style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              backgroundSize: '60px 60px',
            }}
          />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Ready to Experience Bengal?
              </h2>
              <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
                Book your table now and embark on a culinary journey through authentic Bengali cuisine
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/reservations')}
                  className="bg-white text-amber-900 hover:bg-amber-50 px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105"
                >
                  <Calendar className="mr-2 w-5 h-5" />
                  Reserve a Table
                </Button>
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-amber-900 px-8 py-6 text-lg rounded-lg transition-all duration-300"
                >
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </section>
      </FadeIn>
    </div>
  );
};

export default HomePage;
